import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { exhaustMap, filter, map, of, switchMap } from 'rxjs';
import { match } from 'ts-pattern';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';

import { InvoiceActions } from '../actions/invoice.actions';
import { selectInvoiceState } from '../selectors/invoice.selectors';
import { InvoiceCompaniesService } from '../../services/invoice-companies.service';
import { InvoiceDownloadService } from '../../services/invoice-download.service';
import { InvoiceSurveysService } from '../../services/invoice-surveys.service';

@Injectable()
export class InvoiceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _surveysService = inject(InvoiceSurveysService);
  private readonly _companiesService = inject(InvoiceCompaniesService);
  private readonly _downloadService = inject(InvoiceDownloadService);
  private readonly _authSelectorsFacade = inject(AuthSelectorsFacade);

  public searchSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(InvoiceActions.searchSurveys),
      switchMap(({ filters, page }) =>
        this._surveysService.get(filters, page).pipe(
          map((surveys) => InvoiceActions.searchSurveysSuccess({ surveys })),
          this.psCatchError(
            (error) =>
              of(
                InvoiceActions.searchSurveysFailure({
                  error: error?.message ?? 'Unknown error',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadNextPage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(InvoiceActions.loadNextPage),
      concatLatestFrom(() => this._store.select(selectInvoiceState)),
      filter(([, { filters }]) => filters != null),
      exhaustMap(([, { filters, pagination }]) =>
        this._surveysService.get(filters!, pagination.page).pipe(
          map((surveys) => InvoiceActions.searchSurveysSuccess({ surveys })),
          this.psCatchError(
            (error) =>
              of(
                InvoiceActions.searchSurveysFailure({
                  error: error?.message ?? 'Unknown error',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadCompanies$ = createEffect(() =>
    this._actions$.pipe(
      ofType(InvoiceActions.loadCompanies),
      exhaustMap(() =>
        this._companiesService.get().pipe(
          map((companies) =>
            InvoiceActions.loadCompaniesSuccess({ companies }),
          ),
          this.psCatchError(
            (error) =>
              of(
                InvoiceActions.loadCompaniesFailure({
                  error: error?.message ?? 'Unknown error',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public downloadReport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(InvoiceActions.downloadReport),
      switchMap(({ downloadType, params }) => {
        const token = this._authSelectorsFacade
          .user()
          .map((user) => user.token)
          .unwrapOr('');

        return match(downloadType)
          .with('survey-finance', () =>
            this._downloadService.downloadSurveyFinanceReport(params, token),
          )
          .with('supplier-pdf', () =>
            this._downloadService.downloadSupplierPdf(params),
          )
          .with('buyer-xlsx', () =>
            this._downloadService.downloadBuyerXlsx(params),
          )
          .with('supplier-xlsx', () =>
            this._downloadService.downloadSupplierXlsx(params),
          )
          .with('reward-csv', () =>
            this._downloadService.downloadRewardCsv(params),
          )
          .exhaustive()
          .pipe(
            map(() => InvoiceActions.downloadReportSuccess({ downloadType })),
            this.psCatchError(
              (error) =>
                of(
                  InvoiceActions.downloadReportFailure({
                    downloadType,
                    error: error?.message ?? 'Unknown error',
                  }),
                ),
              { notify: true },
            ),
          );
      }),
    ),
  );

  public regenerateInvoice$ = createEffect(() =>
    this._actions$.pipe(
      ofType(InvoiceActions.regenerateInvoice),
      exhaustMap(({ params }) =>
        this._downloadService.regenerateInvoice(params).pipe(
          map(({ status }) =>
            InvoiceActions.regenerateInvoiceSuccess({
              message: `${status.length} surveys regenerated: ${status.join(', ')}`,
            }),
          ),
          this.psCatchError(
            (error) =>
              of(
                InvoiceActions.regenerateInvoiceFailure({
                  error: error?.message ?? 'Unknown error',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );
}
